import { call, put, delay } from 'redux-saga/effects';
import ActionTypes from '@ziynx/action-types';
import type {
  MediaUploadReference, MediaUploadStatus, ContentType, CroppedArea,
} from '@ziynx/flow-types/media';
import type { MongoIdType } from '@ziynx/flow-types/globals';

async function _generateImageBuffer(imageFile?: File): Promise<ArrayBuffer> {
  if (!imageFile) throw new Error('No raw image file passed');

  const buffer = await imageFile.arrayBuffer();

  return buffer;
}

// TODO: optimize this process
async function _generateVideoBuffer(videoFile?: File): Promise<ArrayBuffer> {
  if (!videoFile) throw new Error('No raw video file passed');

  const buffer = await videoFile.arrayBuffer();

  return buffer;
}

export function* getPresignedUrl(action: {
  body: {
    type: ContentType,
    cropDimensions?: CroppedArea,
  },
  mediaUploadReference: MediaUploadReference,
}): Generator<any, void, empty> {
  try {
    const response: {
      uploadUrl: string, // "https://{BUCKET}.s3.amazonaws.com/{KEY}?{SIGNED_PARAMS}", <- Send video asset here
      assetId: string, // "media/{ASSET_ID}", <- Use this assetId to patch profile/me
      type: string, // "video/mp4" or image/jpg
    } = yield call(() => global.APIService.apiRequest('CREATE_MEDIA_URL', 'POST', {
      body: action?.body,
    }));

    let assetArrayBuffer;

    switch (action?.mediaUploadReference?.mediaType) {
    case 'video':
      assetArrayBuffer = yield _generateVideoBuffer(action?.mediaUploadReference?.videoFile);
      break;
    case 'image':
    default:
      assetArrayBuffer = yield _generateImageBuffer(action?.mediaUploadReference?.imageFile);
      break;
    }

    yield put({
      type: ActionTypes.media.uploadMediaAsset.START,
      mediaUploadReference: {
        ...(action?.mediaUploadReference ?? {}),
        uploadUrl: response?.uploadUrl,
        type: response?.type,
        assetId: response?.assetId,
        assetArrayBuffer,
      },
    });

    yield put({ type: ActionTypes.media.getPresignedUrl.SUCCESS });
  } catch (error) {
    yield put({
      type: ActionTypes.media.getPresignedUrl.ERROR,
      response: {
        error: new Error('Get presigned url unsuccessful'),
      },
    });
  }
}

export function* uploadMediaAsset(action: {
  mediaUploadReference: MediaUploadReference,
}): Generator<any, void, empty> {
  const { mediaUploadReference } = action;
  const { uploadUrl, assetArrayBuffer, type } = mediaUploadReference || {};

  try {
    yield call(() => APIService.apiRequest(uploadUrl, 'PUT', {
      body: assetArrayBuffer,
      contentType: type,
      externalRoute: true,
    }));

    yield put({
      type: ActionTypes.media.uploadMediaAsset.SUCCESS,
      mediaUploadReference,
    });
  } catch (error) {
    // TODO: yield put({ type: ActionTypes.media.clearVideo });
    yield put({
      type: ActionTypes.media.uploadMediaAsset.ERROR,
      response: {
        error,
      },
    });
  }
}

export function* checkUploadStatus(action: {
  retries: number,
  mediaUploadReference: MediaUploadReference,
}): Generator<any, void, empty> {
  const { mediaUploadReference } = action;
  const { assetId, destination } = mediaUploadReference || {};
  let retriesUsed: number = action?.retries ?? 0;
  try {
    const response: {
      status: MediaUploadStatus,
    } = yield call(() => APIService.apiRequest('GET_UPLOAD_STATUS', 'GET', {
      routeParams: {
        asset_id: assetId,
      },
    }));

    switch (response?.status) {
    case 'ERROR':
      yield put({
        type: ActionTypes.media.checkUploadStatus.ERROR,
        response,
        mediaUploadReference,
      });
      break;
    case 'COMPLETE':
    case 'UPLOADED': {
      if (destination === 'companyProfileImage') {
        yield put({
          type: ActionTypes.company.patchImage.START,
          body: [
            {
              op: 'replace',
              path: 'profileImage',
              value: assetId,
            },
          ],
        });
      } else if (destination === 'originalVideo') {
        yield put({
          type: ActionTypes.originals.patch.START,
          body: [
            {
              op: 'replace',
              path: 'video',
              value: assetId,
            },
          ],
        });
      }
      // else if (destination === 'originalImage') {
      //   console.log('try to patch this', assetId);
      //   yield put({
      //     type: ActionTypes.originals.patch.START,
      //     body: [
      //       {
      //         op: 'replace',
      //         path: 'thumbnail',
      //         value: assetId,
      //       },
      //     ],
      //   });
      // }

      yield put({
        type: ActionTypes.media.checkUploadStatus.SUCCESS,
        mediaUploadReference: {
          ...(mediaUploadReference ?? {}),
          fetching: false,
        },
        response,
      });
      break;
    }
    default:
      retriesUsed += 1;
      yield delay(5000);
      yield put({
        type: ActionTypes.media.checkUploadStatus.START,
        retries: retriesUsed,
        mediaUploadReference,
      });
      break;
    }
  } catch (error) {
    yield put({
      type: ActionTypes.profile.get.ERROR,
      error,
    });
  }
}

export function* editMediaAsset(action: {
  type: string,
  assetId: MongoIdType,
  body: Array<
    {
      op: 'replace' | 'remove',
      path: 'cropDimensions.top' | 'cropDimensions.left' | 'cropDimensions.width' | 'cropDimensions.height',
      value: number,
    }>,
  mediaUploadReference: MediaUploadReference,
}): Generator<any, void, empty> {
  const { body, assetId, mediaUploadReference } = action;
  const { uploadReferenceId } = mediaUploadReference;

  try {
    yield call(() => APIService.apiRequest('EDIT_MEDIA_ASSET', 'PATCH', {
      routeParams: {
        asset_id: assetId,
      },
      body: body ?? [],
    }));

    yield put({
      type: ActionTypes.media.editMediaAsset.SUCCESS,
      mediaUploadReference,
    });

    if (uploadReferenceId === 'companyProfileImage') {
      yield put({
        type: ActionTypes.company.patchImage.START,
        body: [
          {
            op: 'replace',
            path: 'profileImage',
            value: assetId,
          },
        ],
      });
    }
  } catch (error) {
    yield put({
      type: ActionTypes.media.editMediaAsset.ERROR,
      error,
    });
  }
}
